import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Alert, Button, Card, Checkbox, Empty, List, Rate, Space, Spin, Tag, Typography, message } from "antd";

import { WorkoutSource } from "@fitness-app/data-models/entities/ClientWorkout";

import { type SimulatorTabProps } from "../types";
import {
  allExerciseIds,
  clearWorkoutRate,
  createCompletedWorkoutActivity,
  doneExerciseIds,
  listRecordingsForDay,
  listWorkoutsForDay,
  rateWorkout,
  setWorkoutActivityExercises,
  type SimulatorWorkout,
} from "../writes/workoutWrites";
import SimulatorExerciseRecordingControl from "./SimulatorExerciseRecordingControl";

const SOURCE_LABEL: Record<WorkoutSource, string> = {
  [WorkoutSource.FromActivity]: "zapisany",
  [WorkoutSource.FromSchedule]: "z harmonogramu",
  [WorkoutSource.FromException]: "z wyjątku tygodnia",
};

const SimulatorWorkoutTab = ({ target, date, createdBy, onWritten }: SimulatorTabProps) => {
  const queryClient = useQueryClient();

  const workoutsKey = ["simulator", "workouts", target.id, date];
  const recordingsKey = ["simulator", "recordings", target.id, date];

  const workouts = useQuery({
    queryKey: workoutsKey,
    queryFn: () => listWorkoutsForDay(target.id, target.activeTrainingProgramId, date),
  });

  const recordings = useQuery({
    queryKey: recordingsKey,
    queryFn: () => listRecordingsForDay(target.id, date),
  });

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: workoutsKey });
    void queryClient.invalidateQueries({ queryKey: recordingsKey });
    onWritten();
  };

  const save = useMutation({
    mutationFn: async ({ item, completedIds }: { item: SimulatorWorkout; completedIds: Set<string> }) => {
      // Trening z harmonogramu nie ma jeszcze wiersza — pierwsze odhaczenie go tworzy,
      // dokładnie jak u klientki, która otwiera zaplanowany trening i zaczyna go odklikiwać.
      if (!item.activityId) {
        if (completedIds.size === 0) {
          return;
        }
        await createCompletedWorkoutActivity({
          workout: item.workout,
          completedIds,
          programId: target.activeTrainingProgramId,
          traineeId: target.id,
          userId: target.userId,
          clientEmail: target.email,
          date,
          createdBy,
          mode: "single",
        });
        return;
      }

      await setWorkoutActivityExercises({
        activityId: item.activityId,
        completedIds,
        traineeId: target.id,
        date,
        createdBy,
        mode: "single",
      });
    },
    onSuccess: refresh,
    onError: (error: Error) => void message.error(error.message),
  });

  const rate = useMutation({
    mutationFn: ({ item, value }: { item: SimulatorWorkout; value: number }) =>
      rateWorkout({
        item,
        rate: value,
        comment: "",
        programId: target.activeTrainingProgramId,
        traineeId: target.id,
        userId: target.userId,
        date,
        createdBy,
        mode: "single",
      }),
    onSuccess: () => {
      void message.success("Ocena zapisana");
      refresh();
    },
    onError: (error: Error) => void message.error(error.message),
  });

  const clearRate = useMutation({
    mutationFn: (item: SimulatorWorkout) => clearWorkoutRate(item),
    onSuccess: () => {
      void message.success("Ocena cofnięta");
      refresh();
    },
    onError: (error: Error) => void message.error(error.message),
  });

  if (workouts.isLoading) {
    return <Spin />;
  }

  if (workouts.isError) {
    return <Alert type="error" showIcon message={(workouts.error as Error).message} />;
  }

  const recordingFor = (item: SimulatorWorkout, catalogExerciseId: string | null) =>
    recordings.data?.find(
      (recording) => recording.parentExerciseId === catalogExerciseId && recording.workoutId === item.workoutId,
    ) ?? null;

  const toggleExercise = (item: SimulatorWorkout, exerciseId: string, done: boolean) => {
    const next = doneExerciseIds(item);
    if (done) {
      next.add(exerciseId);
    } else {
      next.delete(exerciseId);
    }
    save.mutate({ item, completedIds: next });
  };

  return (
    <Space direction="vertical" size={16} className="w-full">
      <Alert
        type="info"
        showIcon
        message="Do tygodniowego licznika wchodzi tylko trening odhaczony w całości"
        description="Częściowe wykonanie podbija dzienny licznik ćwiczeń, ale nie zalicza treningu. Serie idą za ćwiczeniem — odhaczone ćwiczenie ma odhaczone wszystkie serie."
      />

      {workouts.data?.length ? (
        workouts.data.map((item) => {
          const doneCount = item.exercises.filter((exercise) => exercise.done).length;
          const allDone = item.exercises.length > 0 && doneCount === item.exercises.length;

          return (
            <Card
              key={`${item.workoutId}:${item.activityId ?? "scheduled"}`}
              size="small"
              title={
                <Space size={8} wrap>
                  <span>{item.name}</span>
                  <Tag color={allDone ? "green" : "default"}>
                    {doneCount} / {item.exercises.length} ćwiczeń
                  </Tag>
                  <Tag>{SOURCE_LABEL[item.source]}</Tag>
                </Space>
              }
              extra={
                <Space>
                  <Button
                    size="small"
                    type="primary"
                    loading={save.isLoading}
                    onClick={() => save.mutate({ item, completedIds: allExerciseIds(item) })}
                  >
                    Odhacz cały
                  </Button>
                  <Button
                    size="small"
                    danger
                    disabled={doneCount === 0}
                    loading={save.isLoading}
                    onClick={() => save.mutate({ item, completedIds: new Set() })}
                  >
                    Wyczyść
                  </Button>
                </Space>
              }
            >
              {item.exercises.length ? (
                <List
                  size="small"
                  dataSource={item.exercises}
                  renderItem={(exercise) => {
                    const recording = exercise.requiresRecording
                      ? recordingFor(item, exercise.catalogExerciseId)
                      : null;

                    return (
                      <List.Item>
                        <Space direction="vertical" size={4} className="w-full">
                          <Space size={8} wrap>
                            <Checkbox
                              checked={exercise.done}
                              disabled={save.isLoading}
                              onChange={(event) => toggleExercise(item, exercise.id, event.target.checked)}
                            >
                              {exercise.name}
                            </Checkbox>
                            {exercise.numberOfSeries ? (
                              <Typography.Text type="secondary">{exercise.numberOfSeries} serii</Typography.Text>
                            ) : null}
                          </Space>

                          {exercise.requiresRecording ? (
                            <div className="pl-6">
                              {recordings.isError ? (
                                <Typography.Text type="danger">
                                  Nie udało się pobrać nagrań: {(recordings.error as Error).message}
                                </Typography.Text>
                              ) : recording ? (
                                <SimulatorExerciseRecordingControl
                                  recording={recording}
                                  traineeId={target.id}
                                  date={date}
                                  createdBy={createdBy}
                                  onDone={refresh}
                                />
                              ) : (
                                <Typography.Text type="secondary">
                                  Wymaga nagrania, ale nie ma zaplanowanego wiersza na ten dzień — zakłada go
                                  workouts-service przy replikacji planu.
                                </Typography.Text>
                              )}
                            </div>
                          ) : null}
                        </Space>
                      </List.Item>
                    );
                  }}
                />
              ) : (
                <Empty description="Trening bez ćwiczeń" image={Empty.PRESENTED_IMAGE_SIMPLE} />
              )}

              <div className="mt-3 border-t pt-3">
                <Space size={12} wrap align="center">
                  <Typography.Text strong>Ocena treningu</Typography.Text>
                  <Rate
                    value={item.rate ?? 0}
                    disabled={!item.activityId || rate.isLoading}
                    onChange={(value) => rate.mutate({ item, value })}
                  />
                  {item.rate ? (
                    <Button size="small" danger loading={clearRate.isLoading} onClick={() => clearRate.mutate(item)}>
                      Cofnij ocenę
                    </Button>
                  ) : null}
                </Space>
                {!item.activityId ? (
                  <Typography.Text type="secondary" className="block">
                    Ocenę wystawia się do zapisanej aktywności — najpierw odhacz choć jedno ćwiczenie.
                  </Typography.Text>
                ) : null}
              </div>
            </Card>
          );
        })
      ) : (
        <Card size="small">
          <Empty
            description="Brak treningów na ten dzień — ani zapisanych, ani wynikających z harmonogramu planu"
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        </Card>
      )}
    </Space>
  );
};

export default SimulatorWorkoutTab;
